import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';
import { AlertSeverity, AlertSeverityType } from 'utils/alert_severity';

const linkInteractionStyles = css`
    transition: color 0.2s ease;

    &:focus {
        outline: 0.2rem solid ${tokens.text.link};
        outline-offset: -0.2rem;
    }
`;

const descriptionStyles = css`
    color: ${tokens.text.primary};
    font-size: 1.4rem;
    line-height: 1.8rem;
    transition: color 0.2s ease;
`;

export const ExternalLinkIcon = styled(FontAwesomeIcon)`
    margin-left: 0.4rem;
    width: 1.4rem;
    height: 1.4rem;
    opacity: 0;
    transition: all 0.2s ease;
    color: ${tokens.icon.actionActive};
    flex-shrink: 0;
`;

// Main container
export const AlertContainer = styled.div<{ $depth?: number }>`
    border-bottom: 0.1rem solid ${tokens.border.default};
    background-color: ${tokens.background.paperElevation2};
    padding-left: ${({ $depth }) => ($depth ? `${$depth * 1.6}rem` : '0')};
    width: 100%;
    box-sizing: border-box;
`;

// Header section
export const AlertHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 1.6rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
    color: ${tokens.text.primary};
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 700;
    line-height: 2rem;

    &:hover {
        background-color: ${tokens.action.hover};
    }

    &:focus {
        outline: 0.2rem solid ${tokens.text.link};
        outline-offset: -0.2rem;
    }
`;

export const HeadingContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 0.4rem;
    width: 100%;
    margin-bottom: 0.4rem;
`;

export const AssetName = styled.h3`
    color: ${tokens.text.primary};
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1.8rem;
    margin: 0;
`;

// Content section
export const AlertContent = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 1.6rem 0.8rem 1.6rem 0;
    box-sizing: border-box;
    overflow: hidden;
`;

export const Description = styled.span`
    ${descriptionStyles}
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
`;

export const Timestamp = styled.span`
    font-size: 1.2rem;
    color: ${tokens.text.secondary};
    margin-top: 0.8rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// Icons and indicators
export const IconsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.8rem;
`;

export const IconButton = styled.a`
    ${linkInteractionStyles}
    color: ${tokens.text.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    z-index: 2;
`;

export const MemoPadIcon = styled(FontAwesomeIcon)`
    width: 1.6rem;
    height: 1.6rem;
`;

export const NotificationIcon = styled.div`
    display: flex;
    align-items: center;
    gap: 0.2rem;
    height: 2.1rem;
    color: ${tokens.text.primary};
`;

export const NotificationBell = styled(FontAwesomeIcon)`
    color: ${tokens.icon.actionActive};
    width: 1.6rem;
    height: 1.6rem;
`;

export const AlertCount = styled.span`
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 400;
    line-height: 2rem;
`;

export const SeverityLozenge = styled.span<{ $severity: AlertSeverityType }>`
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.8rem;
    border-radius: 0.4rem;
    font-size: 1.2rem;
    white-space: nowrap;
    background-color: ${({ $severity }) => {
        switch ($severity) {
            case AlertSeverity.CRITICAL:
                return tokens.severity.critical;
            case AlertSeverity.ALARM:
                return tokens.severity.alarm;
            case AlertSeverity.WARNING:
                return tokens.severity.moderate;
            default:
                return tokens.text.secondary;
        }
    }};
    color: ${tokens.text.inverse};
`;

export const SeverityDot = styled(FontAwesomeIcon)<{ $severity?: AlertSeverityType }>`
    color: ${({ $severity }) => {
        switch ($severity) {
            case AlertSeverity.CRITICAL:
                return tokens.severity.critical;
            case AlertSeverity.ALARM:
                return tokens.severity.alarm;
            case AlertSeverity.WARNING:
                return tokens.severity.moderate;
            default:
                return tokens.text.secondary;
        }
    }};
    width: 1.2rem;
    height: 1.2rem;
`;

// Layout components
export const TopRow = styled.div`
    display: flex;
    justify-content: stretch;
    align-items: stretch;
    gap: 0.8rem;
    width: 100%;
`;

export const AlertInfoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    transition: background-color 0.2s ease;
    padding: 0.4rem;
    position: relative;

    &:hover {
        border-radius: 0.5rem;
        background-color: ${tokens.action.hover};

        ${ExternalLinkIcon} {
            opacity: 1;
        }
    }
`;

export const OverlayLink = styled(Link)`
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: auto;
    cursor: pointer;

    &:focus {
        outline: 0.2rem solid ${tokens.text.link};
        outline-offset: -0.2rem;
        border-radius: 0.8rem;
    }
`;

export const TooltipArea = styled.span`
    position: relative;
    z-index: 2;
    pointer-events: auto;
    flex: 1;
    min-width: 0;

    &.description-tooltip-area {
        cursor: pointer; /* Apply pointer only for this class */
    }

    /* Styles for main alert indicator text */
    .alert-item-indicator-text {
        font-weight: 500;
        max-width: 35rem; /* Main alert width */
        display: inline-block;
        vertical-align: bottom;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    /* Styles for child alert indicator text */
    .child-alert-indicator-text {
        font-weight: 500; /* Assuming same font weight */
        max-width: 28rem; /* Child alert width */
        display: inline-block;
        vertical-align: bottom;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

export const AlertInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
`;

export const ChildAlertInfoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    transition: background-color 0.2s ease;
    padding: 0.4rem;

    &:hover {
        border-radius: 0.5rem;
        background-color: ${tokens.action.hover};

        ${ExternalLinkIcon} {
            opacity: 1;
        }
    }
`;

export const ChildAlertInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
    overflow: hidden;
`;

export const ExpanderRow = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0;
    width: 100%;
    padding: 0;
    margin-top: 0.4rem;
    overflow: hidden;
`;

export const ExpandButton = styled.button<{ $expanded?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    background: ${tokens.input.background};
    border: none;
    padding: 0.6rem 0.8rem;
    border-radius: ${({ $expanded }) => ($expanded ? '0.8rem 0.8rem 0 0' : '0.8rem')};
    color: ${tokens.text.link};
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 1.4rem;
    line-height: 2.4rem;
    letter-spacing: 0.04rem;
    text-transform: uppercase;
    margin-bottom: 0;
    width: fit-content;
    z-index: 2;

    &:hover {
        background-color: ${tokens.action.hover};
    }

    &:focus {
        outline: 0.2rem solid inherit;
        outline-offset: -0.2rem;
    }
`;

export const ChevronIcon = styled(FontAwesomeIcon)<{ $expanded?: boolean }>`
    width: 1.2rem;
    height: 1.2rem;
    transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0)')};
    transition: transform 0.2s ease;
`;

export const ExpandedContent = styled.div`
    background-color: ${tokens.input.background};
    padding: 0.8rem 0;
    border-radius: 0.8rem 0 0.8rem 0.8rem;
    align-self: stretch;
    margin-top: 0;
    overflow: hidden;
    max-width: 100%;
    box-sizing: border-box;
`;

export const ChildAlertContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    padding: 0.8rem 1.6rem;
    overflow: hidden;
    max-width: 100%;
    box-sizing: border-box;

    &:last-child {
        border-bottom: none;
        padding-bottom: 0;
    }

    &:first-child {
        padding-top: 0;
    }
`;

export const TimelineDot = styled(FontAwesomeIcon)`
    color: ${tokens.error.default};
    height: 1.2rem;
    width: 1.2rem;
    margin-right: 0.4rem;
`;
